import { useMemo } from 'react';
import useStatsQuery from './useStatsQuery';
import useSession from '#/common/hooks/useSession';
import { DashboardStatsInput } from '#/graphql/types';
import { Item, ItemKeys } from './useDashboardItems';

type StatsKey = keyof DashboardStatsInput;

const map: Record<ItemKeys, StatsKey[]> = {
  'assets-per-condition': ['assetsPerCondition'],
  'assets-per-location': ['assetsPerLocation', 'totalAssets'],
  'assets-per-type': ['assetsPerType'],
  'assets-per-expenses': ['assetsPerExpenses'],
  'work-orders-per-category': ['workOrdersPerCategory'],
  'work-orders-per-priority': ['workOrdersPerPriority', 'totalWorkOrders'],
  'work-orders-per-state': ['workOrdersPerState'],
  'work-orders-per-due-date': ['workOrdersPerDueDate'],
  'my-work-orders-per-category': ['workOrdersPerCategory'],
  'my-work-orders-per-priority': ['workOrdersPerPriority', 'totalWorkOrders'],
  'my-work-orders-per-state': ['workOrdersPerState'],
  'my-work-orders-per-due-date': ['workOrdersPerDueDate'],
  'findings-per-type': ['findingsPerType'],
  'findings-per-priority': ['findingsPerPriority', 'totalFindings'],
  'findings-per-asset-model': ['findingsPerAssetModel'],
  'work-orders-sla-data': ['workOrdersSLAData'],
};

/**
 * Hook to query a single dashboard stat with automatic skip logic
 * Reduces duplication by handling common patterns for dashboard queries
 */
const useSingleStatsQuery = (key: ItemKeys, visibleItems: Item[]) => {
  const { user } = useSession();

  const skip = !user?.id || visibleItems.every((i) => i.key !== key);
  const statKeys = map[key];

  const stats: DashboardStatsInput = useMemo(() => {
    return statKeys.reduce(
      (res, k) => ({
        ...res,
        [k]: true,
      }),
      {},
    );
  }, [key, ...statKeys]);

  const showMine = key.startsWith('my-');
  const response = useStatsQuery(stats, showMine ? user?.id : undefined, { skip });

  // Extract and simplify the data structure
  const data = useMemo(() => {
    if (!response.data?.getDashboardStats?.data) return null;

    const statsData = response.data.getDashboardStats.data;
    const result: Partial<Record<StatsKey, any>> = {};

    // Handle work order stats that have 'all' and 'user' structure
    if (statsData.all || statsData.user) {
      Object.keys(stats).forEach((k) => {
        const key = k as StatsKey;
        if (stats[key]) {
          const allValue = (statsData.all as any)?.[key];
          const userValue = (statsData.user as any)?.[key];

          if (showMine) {
            result[key] = userValue !== undefined ? userValue : allValue;
          } else if (allValue !== undefined) {
            result[key] = allValue;
          } else if (userValue !== undefined) {
            result[key] = userValue;
          }
        }
      });
    } else {
      // Handle regular stats (assets, findings)
      Object.keys(stats).forEach((k) => {
        const key = k as StatsKey;
        if (stats[key]) {
          result[key] = (statsData as any)[key];
        }
      });
    }

    return result;
  }, [response.data?.getDashboardStats?.data, stats]);

  return {
    loading: response.loading,
    data,
  };
};

export default useSingleStatsQuery;
